// ============================================
// LAB PANEL (60 / 125 / 160 biomarkers) — Auto-Authorization order
// ============================================
// Beluga visitType: labTestingAutoAuthorization (immediate provider approval,
// no clinical questionnaire). Endpoint: POST {base}/lab/labAuthorization (FE
// goes through the BE proxy POST /beluga/lab-authorization).
// Three fixed panels (LAB_PANEL_TIERS, modelled on dexaslim.com/longevity-panel):
// 60 at $299, 125 at $399, 160 at $499. See docs/BACKEND_LAB_AUTOAUTH_VISIT_PRD.md.
//
// There is NO per-test selection — the patient picks ONE panel and orders the
// whole thing; the categories/highlights are shown purely as "what's included."
// Beluga's endpoint still requires a non-empty formObj.testTypes[], so we send a
// single element identifying the panel. The BE charges the client_plan price for
// the tier's planId (DB price only), so each planId must be seeded there.
//
// The panel is SEX-SPECIFIC (Patrick 2026-06-17): male-only markers (PSA) are
// excluded for female patients. Each category carries `audience` ('all' |
// 'male' | 'female'); the FE asks sex first, then renders + submits the
// sex-appropriate panel. (Only PSA is explicitly male-only in the source
// screenshot; confirm any female-only markers with clinical — PRD §8.2.)

export const LAB_PANEL_PRICE = 299;
export const LAB_PANEL_NAME = '60-Biomarker Comprehensive Blood Panel';

// Panel tiers. `price` must equal the client_plan row for `planId` (the BE
// charges the DB price; the FE amount only drives the wallet sheet). The 60
// panel's full list is LAB_PANEL_CATEGORIES below; the larger panels list
// highlights (verbatim from dexaslim.com/longevity-panel — no full marker list
// is published there).
export const DEFAULT_LAB_PANEL_TIER = '60';
export const LAB_PANEL_TIERS = [
  {
    id: '60',
    planId: 'lab-panel-60',
    tierLabel: 'Foundation',
    biomarkers: 60,
    price: LAB_PANEL_PRICE,
    name: LAB_PANEL_NAME,
  },
  {
    id: '125',
    planId: 'lab-panel-125',
    tierLabel: 'Essential',
    biomarkers: 125,
    price: 399,
    name: '125-Biomarker Advanced Blood Panel',
    highlights: [
      'Advanced lipid particles, ApoB & Lp-PLA2',
      'Fasting insulin, C-peptide, A1c & leptin',
      'Full thyroid with TPO & Tg antibodies',
      'Testosterone, estrogens, DHT, LH, FSH & prolactin',
      'Omega-3 index, RBC magnesium, B12, D & iron panel',
      'Autoimmune screen: ANA, RF, CCP & celiac',
      'Lead, mercury & arsenic',
      'CBC, CMP & full urinalysis',
    ],
  },
  {
    id: '160',
    planId: 'lab-panel-160',
    tierLabel: 'Complete',
    biomarkers: 160,
    price: 499,
    name: '160-Biomarker Complete Longevity Panel',
    highlightsIntro: 'Everything in the 125 panel, plus:',
    highlights: [
      'Lp(a), ApoA1 & ApoB/ApoA1 ratio',
      'NT-proBNP, TMAO & myeloperoxidase',
      'Cystatin C & urine albumin/creatinine',
      'Cortisol, progesterone & AMH',
      'Reverse T3, adiponectin & fructosamine',
      'Vitamins A, E, B6 & CoQ10',
      'CEA, AFP & CA-125 tumor markers',
      'Cadmium & aluminum',
    ],
  },
];

// Unknown / missing ids fall back to the 60 panel.
export const getLabPanelTier = (id) =>
  LAB_PANEL_TIERS.find((t) => t.id === String(id ?? '')) ||
  LAB_PANEL_TIERS.find((t) => t.id === DEFAULT_LAB_PANEL_TIER);

// 16 categories with their biomarkers (verbatim from the order screenshot).
export const LAB_PANEL_CATEGORIES = [
  {
    key: 'aging-longevity',
    label: 'Aging / Longevity Score',
    biomarkers: ['Comprehensive aging biomarker assessment'],
  },
  {
    key: 'lipoproteins',
    label: 'Lipoproteins',
    biomarkers: ['ApoB (Apolipoprotein B)', 'Lp(a) (Lipoprotein(a))'],
  },
  {
    key: 'lipids',
    label: 'Lipids',
    biomarkers: [
      'Total Cholesterol',
      'LDL Cholesterol',
      'HDL Cholesterol',
      'VLDL Cholesterol',
      'Triglycerides',
    ],
  },
  {
    key: 'inflammation',
    label: 'Inflammation',
    biomarkers: ['hs-CRP (High-Sensitivity C-Reactive Protein)', 'Homocysteine', 'Uric Acid'],
  },
  {
    key: 'metabolic',
    label: 'Metabolic',
    biomarkers: ['Insulin', 'Glucose', 'HbA1c (Hemoglobin A1c)', 'HOMA-IR (Insulin Resistance Index)'],
  },
  {
    key: 'thyroid',
    label: 'Thyroid',
    biomarkers: [
      'TSH (Thyroid Stimulating Hormone)',
      'Free T4',
      'Free T3',
      'TPOAb (Thyroid Peroxidase Antibodies)',
    ],
  },
  {
    key: 'nutrients',
    label: 'Nutrients',
    biomarkers: ['Ferritin', 'Vitamin D', 'Folate', 'Vitamin B12'],
  },
  {
    key: 'hormonal',
    label: 'Hormonal',
    biomarkers: [
      'Total Testosterone',
      'Free Testosterone',
      'SHBG (Sex Hormone-Binding Globulin)',
      'LH (Luteinizing Hormone)',
      'FSH (Follicle-Stimulating Hormone)',
    ],
  },
  {
    key: 'liver',
    label: 'Liver',
    biomarkers: [
      'Albumin',
      'ALP (Alkaline Phosphatase)',
      'ALT (Alanine Aminotransferase)',
      'AST (Aspartate Aminotransferase)',
      'Total Bilirubin',
      'Direct Bilirubin',
      'Total Protein',
      'GGT (Gamma-Glutamyl Transferase)',
    ],
  },
  {
    key: 'renal',
    label: 'Renal',
    biomarkers: [
      'Cystatin C',
      'Creatinine',
      'BUN (Blood Urea Nitrogen)',
      'eGFR (Estimated Glomerular Filtration Rate)',
    ],
  },
  {
    key: 'immune-function',
    label: 'Immune Function',
    biomarkers: [
      'WBC Count',
      'Neutrophil Count',
      'Monocyte Count',
      'Lymphocyte Count',
      'Eosinophil Count',
      'Basophil Count',
    ],
  },
  {
    key: 'electrolytes',
    label: 'Electrolytes',
    biomarkers: ['Calcium', 'Chloride', 'Sodium', 'Potassium', 'Bicarbonate'],
  },
  {
    key: 'red-blood-cells',
    label: 'Red Blood Cells',
    biomarkers: [
      'RBC Count',
      'Hemoglobin',
      'Hematocrit',
      'MCV (Mean Corpuscular Volume)',
      'MCH (Mean Corpuscular Hemoglobin)',
      'MCHC (Mean Corpuscular Hemoglobin Concentration)',
      'RDW (Red Cell Distribution Width)',
      'MPV (Mean Platelet Volume)',
    ],
  },
  {
    key: 'blood-clotting',
    label: 'Blood Clotting',
    biomarkers: ['Platelet Count'],
  },
  {
    key: 'cancer-detection',
    label: 'Cancer Detection',
    audience: 'male', // Total PSA is male-only — excluded for female patients
    biomarkers: ['Total PSA (Prostate-Specific Antigen — male only)'],
  },
  {
    key: 'popular-add-ons',
    label: 'Popular add-ons',
    biomarkers: [
      'APOE Genotype',
      'Cortisol',
      'Creatine Kinase',
      'DHEA-S',
      'Estradiol',
      'IGF-1',
      'Mercury',
      'OmegaCheck',
      'Zinc',
    ],
  },
];

// Sex-aware helpers. `sex` is 'male' | 'female' (from sex-at-birth); unknown
// falls back to the full panel.
export const getCategoriesForSex = (sex) =>
  LAB_PANEL_CATEGORIES.filter((c) => !c.audience || c.audience === 'all' || c.audience === sex);

// Required formObj.testTypes[] — the chosen fixed, sex-specific panel (no
// per-test selection). Endpoint requires a non-empty array; we send one panel id.
export const getTestTypesForSex = (sex, tierId) => {
  const label = sex === 'female' ? 'Female' : 'Male';
  return [`${getLabPanelTier(tierId).name} (${label})`];
};

export const LAB_CONSENT_BODY = `Consent for Laboratory Testing and Auto-Authorization

You are requesting a laboratory blood test panel. By proceeding, you authorize a licensed provider affiliated with Beluga Health to review your request and issue a lab requisition for the selected biomarker panel so that an accredited laboratory can collect and analyze your blood sample.

What this is and isn't
- This is an order for laboratory testing only. It is not a diagnosis, treatment, or prescription, and it does not establish an ongoing provider-patient treatment relationship.
- Results are provided for informational and wellness purposes. Abnormal results may warrant follow-up with your personal physician. This service does not replace routine medical care.

Your acknowledgements
- You confirm the personal information provided is accurate and that you are at least 18 years old.
- You understand a provider will auto-authorize the requisition based on the information you provide; you agree to present valid identification at the draw site if requested.
- You understand testing availability and certain biomarkers may vary by state and laboratory, and that some markers (e.g., prostate-specific antigen) may only be appropriate for specific patients.
- You consent to the collection of a blood sample at a partner laboratory or draw site and to the secure transmission of your results.

By selecting below, you confirm that you have read and understood this information and wish to proceed with the laboratory testing order.`;

// ---------- Steps ----------

const panelOverviewStep = {
  id: 'panel-overview',
  type: 'lab-panel-overview',
  question: 'Choose your blood panel',
  subtitle: 'One blood draw, one flat price. Pick how deep you want to go.',
};

const basicContactStep = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send your draw-site details and results updates.",
};

const sexAtBirthStep = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: "Let's personalize your panel — what is your sex assigned at birth?",
  subtitle: 'We tailor the biomarkers and reference ranges (e.g., PSA is included for males only).',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ],
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Your address',
  subtitle: 'Used to match you with an in-network draw site.',
};

const consentStep = {
  id: 'lab-consent',
  type: 'consent-dropdown',
  question: 'Laboratory Testing — Informed Consent',
  consentText: LAB_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the above information and wish to proceed.', value: 'consent' },
    { label: 'I do NOT wish to proceed.', value: 'decline', disqualifies: true },
  ],
};

// Required gov-ID photo (Beluga requirement, 2026-06-27) — relayed post-submit
// via uploadVisitPhoto, same pipeline as the other flows.
const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
};

// §lab-panel-dexa — optional $179 DEXA scan, New York residents only. The
// form drops this step for everyone else (see labPanelDexaAvailable).
const dexaUpsellStep = {
  id: 'dexa-upsell',
  type: 'lab-dexa-upsell',
  question: 'Add a DEXA body composition scan?',
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review & pay',
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete',
};

export const labPanelFlow = [
  sexAtBirthStep, // first — drives the sex-specific panel shown next
  panelOverviewStep,
  basicContactStep,
  shippingAddressStep,
  identityVerificationStep,
  consentStep,
  dexaUpsellStep, // NY only — filtered out by the form elsewhere
  checkoutStep,
  completeStep,
];

export const labPanelFlowById = labPanelFlow.reduce((acc, s) => {
  acc[s.id] = s;
  return acc;
}, {});

export const isDisqualifyingAnswer = (questionId, answer) => {
  if (questionId === 'lab-consent' && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'You have indicated that you do not wish to proceed with the laboratory testing order.',
    };
  }
  return { disqualified: false };
};

export default labPanelFlow;
